import React from 'react';
import AshaIcon from '@/components/ui/AshaIcon';
import Icon from '@/components/ui/AppIcon';


const footerLinks = {
  Services: [
    'Physical Therapy',
    'Occupational Therapy',
    'Sports Injury Rehab',
    'Work Injury Therapy',
    'Pain Management',
    'Post-Surgical Rehab',
  ],
  Company: ['About ASHA', 'Clinical Team', 'Careers', 'News & Research', 'Community Outreach'],
  Patients: ['Find a Clinic', 'Request Appointment', 'Patient Portal', 'Insurance Info', 'Patient Resources', 'Telehealth'],
  Physicians: ['Submit Referral', 'Physician Portal', 'Referral FAQs', 'Clinical Partners', 'Contact Liaison'],
};

const serviceIcons = [
  'physical-therapy',
  'occupational-therapy',
  'sports-rehab',
  'work-injury',
  'pain-management',
  'post-surgical-rehab',
] as const;

function FacebookSvg({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function TwitterSvg({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
    </svg>
  );
}

function LinkedinSvg({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

function InstagramSvg({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

const socialLinks = [
  { id: 'social-fb', Icon: FacebookSvg, label: 'Facebook', href: '#' },
  { id: 'social-tw', Icon: TwitterSvg, label: 'Twitter', href: '#' },
  { id: 'social-li', Icon: LinkedinSvg, label: 'LinkedIn', href: '#' },
  { id: 'social-ig', Icon: InstagramSvg, label: 'Instagram', href: '#' },
];

export default function PublicFooter() {
  return (
    <footer id="contact" style={{ background: 'var(--primary)' }}>
      {/* Main Footer */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-5">
              <img
                src="/assets/images/LOGOvertical-1777629360990.png"
                alt="ASHA Recovery Group"
                className="h-24 w-auto object-contain"
              />
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-xs">
              A network of specialized physical therapy and rehabilitation clinics dedicated to restoring mobility and rebuilding lives through evidence-based care.
            </p>
            <div className="space-y-2.5">
              <div className="flex items-center gap-2.5">
                <AshaIcon name="physician-partner" size={14} color="rgba(255,255,255,0.5)" className="flex-shrink-0" />
                <span className="text-white/70 text-sm">(312) 555-0100</span>
              </div>
              <div className="flex items-center gap-2.5">
                <AshaIcon name="patient-portal" size={14} color="rgba(255,255,255,0.5)" className="flex-shrink-0" />
                <span className="text-white/70 text-sm">info@asharecoverygroup.com</span>
              </div>
              <div className="flex items-start gap-2.5">
                <AshaIcon name="find-clinic" size={14} color="rgba(255,255,255,0.5)" className="flex-shrink-0 mt-0.5" />
                <span className="text-white/70 text-sm">18 Locations Across Chicagoland</span>
              </div>
            </div>

            {/* Service icons strip */}
            <div className="mt-6 flex flex-wrap gap-2">
              {serviceIcons.map((iconName) => (
                <div
                  key={iconName}
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(27,175,191,0.15)' }}
                  title={iconName.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                >
                  <AshaIcon name={iconName} size={18} color="#1BAFBF" />
                </div>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks)?.map(([category, links]) => (
            <div key={`footer-col-${category}`}>
              <h4 className="text-white font-bold text-sm mb-4 uppercase tracking-wider">{category}</h4>
              <ul className="space-y-2.5">
                {links?.map((link) => (
                  <li key={`footer-link-${category}-${link}`}>
                    <a href="#" className="text-white/55 text-sm hover:text-white transition-colors duration-150 flex items-center gap-2">
                      {category === 'Services' && serviceIcons[footerLinks.Services.indexOf(link)] && (
                        <AshaIcon name={serviceIcons[footerLinks.Services.indexOf(link)]} size={14} color="rgba(255,255,255,0.4)" />
                      )}
                      {category === 'Patients' && link === 'Find a Clinic' && (
                        <AshaIcon name="find-clinic" size={14} color="rgba(255,255,255,0.4)" />
                      )}
                      {category === 'Patients' && link === 'Patient Portal' && (
                        <AshaIcon name="patient-portal" size={14} color="rgba(255,255,255,0.4)" />
                      )}
                      {category === 'Physicians' && link === 'Submit Referral' && (
                        <AshaIcon name="submit-referral" size={14} color="rgba(255,255,255,0.4)" />
                      )}
                      {category === 'Physicians' && link === 'Physician Portal' && (
                        <AshaIcon name="physician-partner" size={14} color="rgba(255,255,255,0.4)" />
                      )}
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {/* Bottom Bar */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs">
            © 2026 ASHA Recovery Group. All rights reserved. HIPAA Compliant.
          </p>
          <div className="flex items-center gap-5">
            {['Privacy Policy', 'Terms of Service', 'Accessibility', 'Cookie Policy']?.map((item) => (
              <a key={`legal-${item}`} href="#" className="text-white/40 text-xs hover:text-white/70 transition-colors duration-150">
                {item}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {socialLinks?.map((social) => {
              const { Icon } = social;
              return (
                <a
                  key={social?.id}
                  href={social?.href}
                  aria-label={social?.label}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white transition-all duration-150"
                  style={{ background: 'rgba(255,255,255,0.07)' }}
                >
                  <Icon size={15} />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}